import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { paintPrizeDisc } from './discRenderer';
import { SEGMENT_COUNT, WHEEL_SEGMENTS } from './wheelConfig';
import {
  animateSpin,
  prefersReducedMotion,
  segmentIndexFromRotation,
  targetRotationForSegment,
} from './spinEngine';
import type { WheelAudio } from './WheelAudio';

export interface LuckyWheelHandle {
  spinTo: (segmentIndex: number) => Promise<void>;
  stop: () => void;
  getRotation: () => number;
}

interface LuckyWheelProps {
  variant: 'preview' | 'full' | 'nav';
  audio?: WheelAudio | null;
  celebrating?: boolean;
  hubLabel?: string;
  hubBusy?: boolean;
  hubDisabled?: boolean;
  onHubClick?: () => void;
}

export const LuckyWheel = forwardRef<LuckyWheelHandle, LuckyWheelProps>(function LuckyWheel(
  { variant, audio, celebrating = false, hubLabel, hubBusy = false, hubDisabled = false, onHubClick },
  ref,
) {
  const uid = useId().replace(/:/g, '');
  const stageRef = useRef<HTMLDivElement>(null);
  const rotorRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointerRef = useRef<HTMLDivElement>(null);
  const rotationRef = useRef(0);
  const cancelRef = useRef<(() => void) | null>(null);
  const lastSegRef = useRef(0);
  const slice = 360 / SEGMENT_COUNT;
  const isNav = variant === 'nav' || variant === 'preview';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    void paintPrizeDisc(canvas, WHEEL_SEGMENTS).then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncPause = () => {
      const hidden = document.hidden || prefersReducedMotion();
      stageRef.current?.classList.toggle('lw-paused', hidden);
    };
    syncPause();
    document.addEventListener('visibilitychange', syncPause);
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    media?.addEventListener?.('change', syncPause);
    return () => {
      document.removeEventListener('visibilitychange', syncPause);
      media?.removeEventListener?.('change', syncPause);
    };
  }, []);

  const applyRotation = useCallback((deg: number) => {
    rotationRef.current = deg;
    if (rotorRef.current) {
      rotorRef.current.style.transform = `rotate(${deg}deg)`;
    }
  }, []);

  const bumpPointer = useCallback(() => {
    const el = pointerRef.current;
    if (!el) return;
    el.classList.remove('lw-pointer-hit');
    void el.offsetWidth;
    el.classList.add('lw-pointer-hit');
  }, []);

  const setSpinning = useCallback((on: boolean) => {
    stageRef.current?.classList.toggle('is-spinning', on);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      getRotation: () => rotationRef.current,
      stop: () => {
        cancelRef.current?.();
        cancelRef.current = null;
        setSpinning(false);
        audio?.stop();
      },
      spinTo: (segmentIndex: number) =>
        new Promise<void>((resolve, reject) => {
          cancelRef.current?.();
          const reduced = prefersReducedMotion();
          const extraTurns = reduced ? 1 : 8 + Math.floor(Math.random() * 2);
          const jitter = reduced ? 0 : (Math.random() - 0.5) * slice * 0.36;
          const durationMs = reduced ? 620 : 5200 + Math.random() * 700;
          const from = rotationRef.current;
          const to = targetRotationForSegment(from, segmentIndex, extraTurns, jitter);
          lastSegRef.current = segmentIndexFromRotation(from);
          setSpinning(true);
          audio?.startSpin();
          let settled = false;

          const stopAnim = animateSpin({
            from,
            to,
            durationMs,
            reducedMotion: reduced,
            onFrame: ({ rotation, progress, segmentIndex: current }) => {
              applyRotation(rotation);
              audio?.setSpinProgress(progress);
              if (current !== lastSegRef.current) {
                lastSegRef.current = current;
                bumpPointer();
                audio?.tick(progress);
              }
            },
            onComplete: (rotation) => {
              if (settled) return;
              settled = true;
              applyRotation(rotation);
              cancelRef.current = null;
              setSpinning(false);
              audio?.stop();
              resolve();
            },
          });

          cancelRef.current = () => {
            stopAnim();
            if (settled) return;
            settled = true;
            cancelRef.current = null;
            setSpinning(false);
            audio?.stop();
            reject(new Error('spin_cancelled'));
          };
        }),
    }),
    [applyRotation, audio, bumpPointer, setSpinning, slice],
  );

  const leds = useMemo(() => [] as { i: number; transform: string; delay: string }[], []);

  const hubInner = (
    <>
      <div className="lw-hub-ring" />
      <div className="lw-hub-dome">
        {variant === 'full' ? (
          <strong>{hubBusy ? '• • •' : hubLabel || 'FIRLAT'}</strong>
        ) : (
          <>
            <strong>VBB</strong>
            <span>PLAY</span>
          </>
        )}
      </div>
    </>
  );

  return (
    <div
      ref={stageRef}
      className={`lw-stage lw-${variant}${variant === 'full' ? ' lw-stage-full' : ''}${celebrating ? ' lw-celebrate' : ''}`}
    >
      {isNav ? null : <div className="lw-aura" aria-hidden="true" />}
      {isNav ? null : <div className="lw-ground" aria-hidden="true" />}
      <div className="lw-assembly">
        <div className="lw-rim-thickness" aria-hidden="true" />
        <svg className="lw-rim" viewBox="0 0 1000 1000" aria-hidden="true">
          <defs>
            <linearGradient id={`${uid}-orange`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffb45a" />
              <stop offset="45%" stopColor="#ff8a1a" />
              <stop offset="100%" stopColor="#e56a00" />
            </linearGradient>
          </defs>
          <circle cx="500" cy="508" r="470" fill="rgba(40, 10, 70, 0.18)" />
          <circle cx="500" cy="500" r="492" fill={`url(#${uid}-orange)`} />
          <circle cx="500" cy="500" r="454" fill="#fff7e8" />
        </svg>

        {leds.length ? (
          <div className="lw-leds" aria-hidden="true">
            {leds.map((led) => (
              <span
                key={led.i}
                className="lw-led"
                style={{ transform: led.transform, animationDelay: led.delay }}
              />
            ))}
          </div>
        ) : null}

        <div className="lw-well">
          <div className="lw-rotor" ref={rotorRef}>
            <canvas ref={canvasRef} className="lw-disc" />
          </div>
          <div className="lw-coat" />
        </div>

        {variant === 'full' && onHubClick ? (
          <button
            type="button"
            className={`lw-hub lw-hub-btn${hubBusy ? ' is-busy' : ''}`}
            onClick={onHubClick}
            disabled={hubDisabled}
            aria-label={hubLabel || 'FIRLAT'}
          >
            {hubInner}
          </button>
        ) : (
          <div className="lw-hub" aria-hidden="true">
            {hubInner}
          </div>
        )}

        {celebrating ? (
          <>
            <span className="lw-burst lw-burst-a" />
            <span className="lw-burst lw-burst-b" />
            <span className="lw-burst lw-burst-c" />
          </>
        ) : null}

        {isNav ? null : (
          <div className="lw-pointer" ref={pointerRef} aria-hidden="true">
            <svg viewBox="0 0 80 90">
              <path d="M40 8 L64 58 L40 46 L16 58 Z" fill="#4d5bff" />
              <path d="M40 16 L52 48 L40 42 L28 48 Z" fill="rgba(255,255,255,0.28)" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
});
